/**
 * SubItemsEditor Component
 *
 * Manages sub-item definitions for an inspection template item.
 * Sub-items represent positions (e.g. 4 wheels) that share the same measurements.
 * Includes presets for common configurations.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  Chip,
  Collapse,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import type { SubItemDefinition } from '@/hooks/useInspections';
import { useTranslation } from 'react-i18next';

interface SubItemsEditorProps {
  subItems: SubItemDefinition[];
  onChange: (subItems: SubItemDefinition[]) => void;
}

function generateId(): string {
  return `si-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface SubItemPreset {
  labelKey: string;
  items: string[];
}

const PRESETS: SubItemPreset[] = [
  {
    labelKey: 'inspectionTemplates.preset4Wheels',
    items: ['Vasen etu', 'Oikea etu', 'Vasen taka', 'Oikea taka'],
  },
  {
    labelKey: 'inspectionTemplates.presetFrontRear',
    items: ['Etu', 'Taka'],
  },
  {
    labelKey: 'inspectionTemplates.presetLeftRight',
    items: ['Vasen', 'Oikea'],
  },
  {
    labelKey: 'inspectionTemplates.presetFrontRearLeftRight',
    items: ['Vasen etu', 'Oikea etu', 'Vasen taka', 'Oikea taka', 'Vara'],
  },
];

export function SubItemsEditor({ subItems, onChange }: SubItemsEditorProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(subItems.length > 0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleAdd = () => {
    const newSubItem: SubItemDefinition = {
      id: generateId(),
      label: '',
      sortOrder: subItems.length,
    };
    onChange([...subItems, newSubItem]);
    setExpanded(true);
  };

  const handleUpdate = (index: number, label: string) => {
    const updated = [...subItems];
    const item = updated[index];
    if (!item) return;
    updated[index] = { ...item, label };
    onChange(updated);
  };

  const handleDelete = (index: number) => {
    onChange(
      subItems
        .filter((_, i) => i !== index)
        .map((s, idx) => ({ ...s, sortOrder: idx }))
    );
  };

  const handleApplyPreset = (preset: SubItemPreset) => {
    const newSubItems: SubItemDefinition[] = preset.items.map((label, idx) => ({
      id: generateId(),
      label,
      sortOrder: idx,
    }));
    onChange(newSubItems);
    setExpanded(true);
    setAnchorEl(null);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {t('inspectionTemplates.subItems')}
        </Typography>
        {subItems.length > 0 && (
          <Chip size="small" label={subItems.length} variant="outlined" />
        )}
        {subItems.length > 0 && (
          <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.25 }}
              aria-label={expanded ? tc('collapse') : tc('expand')}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Collapse in={expanded}>
        <Stack spacing={0.5}>
          {subItems.map((subItem, index) => (
            <Box
              key={subItem.id}
              display="flex"
              gap={1}
              alignItems="center"
            >
              <TextField
                value={subItem.label}
                onChange={(e) => handleUpdate(index, e.target.value)}
                size="small"
                placeholder={t('inspectionTemplates.subItemLabel')}
                sx={{ flex: 1 }}
              />
              <Tooltip title={tc('delete')}>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(index)}
                  color="error"
                  aria-label={tc('delete')}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      </Collapse>

      <Box display="flex" gap={1} mt={0.5}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          {t('inspectionTemplates.addSubItem')}
        </Button>
        <Button
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {t('inspectionTemplates.presets')}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {PRESETS.map((preset) => (
            <MenuItem key={preset.labelKey} onClick={() => handleApplyPreset(preset)}>
              <ListItemText
                primary={t(preset.labelKey)}
                secondary={preset.items.join(', ')}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
}
